import React, { useState, useEffect, useMemo } from "react";
import {
	Box,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	TableSortLabel,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	IconButton,
	Button,
	Alert,
	Grid,
	Chip,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Checkbox,
	ListItemText,
	OutlinedInput,
	FormHelperText,
} from "@mui/material";
import {
	Edit as EditIcon,
	Plus as PlusIcon,
	Search as SearchIcon,
	Check as CheckIcon,
	X as XIcon,
	MapPin,
	Globe,
	VolumeX as AudioIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { ROLES as APP_ROLES } from "../../config/supabase";
import { getIndianStates, getDistrictsForStateName, normalizeSchoolLocation } from "../../data/indiaStateDistrictOptions";
import { fetchActiveAssignedSchoolIdsForExpert } from "../../utils/employeeSchools";

const INDIAN_STATES = getIndianStates();

const SYLLABUS_OPTIONS = ["CBSE", "ICSE", "IGCSE", "IB", "State Board"];

const SORTABLE_COLUMNS = [
	{ key: "name", label: "School" },
	{ key: "schoolId", label: "School ID" },
	{ key: "type", label: "Type" },
	{ key: "owner", label: "Owner" },
	{ key: "district", label: "District" },
	{ key: "grades", label: "Grades" },
	{ key: "principal", label: "Principal" },
	{ key: "contact", label: "Contact" },
	{ key: "website", label: "Website" },
	{ key: "audio", label: "Audio" },
	{ key: "status", label: "Status" },
];

const ManageSchools = () => {
	const { supabase, user } = useAuth();
	const [viewerProfile, setViewerProfile] = useState(null);
	const isFunmiyoEmployee = viewerProfile?.role === APP_ROLES.FUNMIYO_EMPLOYEE;
	const [schools, setSchools] = useState([]);
	const [filteredSchools, setFilteredSchools] = useState([]);
	const [filters, setFilters] = useState({
		search: "",
		status: "",
	});
	const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
	const [selectedSchool, setSelectedSchool] = useState(null);
	const identityFieldsLocked = isFunmiyoEmployee && !!selectedSchool;
	const [openDialog, setOpenDialog] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [schoolTypes, setSchoolTypes] = useState([]);
	const [grades, setGrades] = useState([]);
	const [schoolGrades, setSchoolGrades] = useState([]);
	const STATUS_COLORS = {
		Active: "#10B981",
		Inactive: "#EF4444",
		"Pending Approval": "#F59E0B",
	};
	// Add pagination state
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const [isLoading, setIsLoading] = useState(false);

	const [editForm, setEditForm] = useState({
		name: "",
		address: "",
		school_type_id: "",
		district: "",
		website_link: "",
		landline: "",
		principal_name: "",
		principal_contact: "",
		finance_incharge: "",
		finance_incharge_contact: "",
		store_incharge: "",
		store_incharge_contact: "",
		primary_email: "",
		status: "Active",
		map_location: "",
		selected_grades: [],
		school_unique_id: "",
		state: "",
		block: "",
		accounts_incharge: "",
		accounts_incharge_number: "",
		total_kits_ordered: "",
		syllabus: "",
		audio: "",
	});

	// Add validation states
	const [formErrors, setFormErrors] = useState({
		name: false,
		address: false,
		school_type_id: false,
		state: false,
		district: false,
		selected_grades: false, // Added validation for grades
	});

	const [formErrorMessage, setFormErrorMessage] = useState("");
	/** `true` = expert has no active employee_schools rows; `undefined` = not applicable */
	const [funmiyoExpertNoSchoolLinks, setFunmiyoExpertNoSchoolLinks] =
		useState(undefined);
	const dialogContentRef = React.useRef(null);

	const districtOptions = useMemo(() => {
		const list = getDistrictsForStateName(editForm.state);
		const d = editForm.district;
		if (d && !list.includes(d)) return [d, ...list];
		return list;
	}, [editForm.state, editForm.district]);

	const stateOptions = useMemo(() => {
		const names = INDIAN_STATES.map((s) => s.stateName);
		const current = editForm.state;
		if (current && !names.includes(current)) {
			return [{ stateCode: "__current__", stateName: current }, ...INDIAN_STATES];
		}
		return INDIAN_STATES;
	}, [editForm.state]);

	useEffect(() => {
		if (!user?.id) return;

		const load = async () => {
			try {
				const { data: me, error: meErr } = await supabase
					.from("profiles")
					.select("role, school_id")
					.eq("id", user.id)
					.single();

				if (meErr) throw meErr;
				setViewerProfile(me);

				await Promise.all([
					fetchSchoolTypes(),
					fetchGrades(),
					fetchSchools(me),
				]);
			} catch (e) {
				console.error("ManageSchools: init", e);
			}
		};

		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.id]);

	useEffect(() => {
		applyFilters();
	}, [filters, schools, sortConfig, isFunmiyoEmployee]);

	const fetchSchoolTypes = async () => {
		try {
			const { data, error } = await supabase
				.from("school_types")
				.select("*")
				.order("name");

			if (error) throw error;
			setSchoolTypes(data || []);
		} catch (err) {
			console.error("Error fetching school types:", err);
		}
	};

	const fetchGrades = async () => {
		try {
			const { data, error } = await supabase
				.from("grades")
				.select("*")
				.order("name");

			if (error) throw error;
			setGrades(data || []);
		} catch (err) {
			console.error("Error fetching grades:", err);
		}
	};

	const fetchSchoolGrades = async (schoolId) => {
		try {
			const { data, error } = await supabase
				.from("school_grades")
				.select("*")
				.eq("school_id", schoolId);

			if (error) throw error;
			setSchoolGrades(data || []);

			// Extract grade IDs for the form
			const gradeIds = data.map((sg) => sg.grade_id);
			setEditForm((prev) => ({
				...prev,
				selected_grades: gradeIds,
			}));
		} catch (err) {
			console.error("Error fetching school grades:", err);
		}
	};

	const fetchSchools = async (meOverride) => {
		const me = meOverride !== undefined ? meOverride : viewerProfile;
		const restrictToActiveAssignments = me?.role === APP_ROLES.FUNMIYO_EMPLOYEE;
		try {
			let query = supabase
				.from("schools")
				.select(
					`
        *,
        school_types (
          id,
          name
        ),
        employee_schools (
          active_status,
          profiles:profile_id (
            id,
            full_name,
            role
          )
        ),
        school_grades (
          id,
          grade_id,
          grades:grade_id (
            id,
            name
          )
        ),
        school_visits (
          planned_by,
          planned_by_profile:profiles!planned_by (
            id,
            full_name,
            role
          )
        )
      `
				)
				.order("created_at", { ascending: false });

			if (restrictToActiveAssignments) {
				const assignedIds = await fetchActiveAssignedSchoolIdsForExpert(
					supabase,
					user.id
				);
				setFunmiyoExpertNoSchoolLinks(assignedIds.length === 0);
				if (assignedIds.length === 0) {
					setSchools([]);
					setFilteredSchools([]);
					return;
				}
				query = query.in("id", assignedIds).eq("status", "Active");
			} else {
				setFunmiyoExpertNoSchoolLinks(undefined);
			}

			const { data, error } = await query;

			if (error) throw error;
			setSchools(data || []);
			setFilteredSchools(data || []);
		} catch (err) {
			console.error("Error fetching schools:", err);
		}
	};

	const getSchoolOwnerNames = (school) => {
		const names = new Set();

		school.employee_schools?.forEach((empSchool) => {
			if (empSchool.active_status === false) return;
			if (empSchool.profiles?.full_name) {
				names.add(empSchool.profiles.full_name.trim());
			}
		});

		if (names.size === 0) {
			school.school_visits?.forEach((visit) => {
				if (visit.planned_by_profile?.full_name) {
					names.add(visit.planned_by_profile.full_name.trim());
				}
			});
		}

		return Array.from(names).sort((a, b) => a.localeCompare(b));
	};

	const getSchoolPlanners = (school) => {
		const ownerNames = getSchoolOwnerNames(school);

		if (ownerNames.length === 0) {
			return "No owners assigned.";
		}

		const fromVisits =
			!(school.employee_schools?.some((row) => row.active_status !== false)) &&
			(school.school_visits?.length > 0);
		const suffix = fromVisits ? " (from visits)" : "";

		return `${ownerNames.join(", ")}${suffix}`;
	};

	const getGradeNames = (school) => {
		if (!school.school_grades || school.school_grades.length === 0) {
			return "None";
		}

		const gradeNames = school.school_grades
			.filter((sg) => sg.grades)
			.map((sg) => sg.grades.name)
			.sort();

		if (gradeNames.length === 0) return "None";

		return gradeNames.length <= 3
			? gradeNames.join(", ")
			: `${gradeNames.slice(0, 3).join(", ")}... +${gradeNames.length - 3} more`;
	};

	const getSchoolSortValue = (school, key) => {
		switch (key) {
			case "name":
				return school.name || "";
			case "schoolId":
				return school.school_unique_id || "";
			case "type":
				return school.school_types?.name || school.school_type || "";
			case "owner":
				return getSchoolOwnerNames(school).join(", ") || getSchoolPlanners(school);
			case "district":
				return school.district || "";
			case "grades":
				return getGradeNames(school);
			case "principal":
				return school.principal_name || "";
			case "contact":
				return school.principal_contact || school.landline || "";
			case "website":
				return school.website_link || "";
			case "audio":
				return school.audio || "";
			case "status":
				return school.status || "Active";
			default:
				return "";
		}
	};

	const schoolMatchesSearch = (school, searchLower) => {
		const searchableValues = [
			school.name,
			school.school_unique_id,
			school.school_types?.name,
			school.school_type,
			school.district,
			school.state,
			school.block,
			school.address,
			school.primary_email,
			school.principal_name,
			school.principal_contact,
			school.finance_incharge,
			school.store_incharge,
			school.accounts_incharge,
			school.landline,
			school.website_link,
			school.audio,
			school.syllabus,
			school.status,
			getGradeNames(school),
			getSchoolPlanners(school),
			...getSchoolOwnerNames(school),
		];

		return searchableValues.some((value) =>
			value?.toString().toLowerCase().includes(searchLower)
		);
	};

	const handleSort = (key) => {
		setSortConfig((prev) => ({
			key,
			direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
		}));
		setPage(0);
	};

	const applyFilters = () => {
		let filtered = [...schools];

		if (isFunmiyoEmployee) {
			filtered = filtered.filter((school) => school.status === "Active");
		}

		if (filters.search) {
			const searchLower = filters.search.toLowerCase().trim();
			filtered = filtered.filter((school) =>
				schoolMatchesSearch(school, searchLower)
			);
		}

		if (filters.status) {
			filtered = filtered.filter((school) => school.status === filters.status);
		}

		filtered.sort((a, b) => {
			const aVal = getSchoolSortValue(a, sortConfig.key).toLowerCase();
			const bVal = getSchoolSortValue(b, sortConfig.key).toLowerCase();
			const comparison = aVal.localeCompare(bVal, undefined, {
				numeric: true,
				sensitivity: "base",
			});
			return sortConfig.direction === "asc" ? comparison : -comparison;
		});

		setFilteredSchools(filtered);
	};

	const handleEditClick = (school) => {
		setSelectedSchool(school);
		const { state, district } = normalizeSchoolLocation(
			school.state,
			school.district
		);
		const gradeIdsFromSchool = (school.school_grades || [])
			.map((sg) => sg.grade_id)
			.filter(Boolean);
		setFormErrorMessage("");
		setFormErrors({
			name: false,
			address: false,
			school_type_id: false,
			state: false,
			district: false,
			selected_grades: false,
		});
		setEditForm({
			name: school.name || "",
			address: school.address || "",
			school_type_id: school.school_type_id || "",
			district,
			website_link: school.website_link || "",
			landline: school.landline || "",
			principal_name: school.principal_name || "",
			principal_contact: school.principal_contact || "",
			finance_incharge: school.finance_incharge || "",
			finance_incharge_contact: school.finance_incharge_contact || "",
			store_incharge: school.store_incharge || "",
			store_incharge_contact: school.store_incharge_contact || "",
			primary_email: school.primary_email || "",
			status: school.status || "Active",
			map_location: school.map_location || "",
			selected_grades: gradeIdsFromSchool,
			school_unique_id: school.school_unique_id || "",
			state,
			block: school.block || "",
			accounts_incharge: school.accounts_incharge || "",
			accounts_incharge_number: school.accounts_incharge_number || "",
			total_kits_ordered: school.total_kits_ordered || "",
			syllabus: school.syllabus || "",
			audio: school.audio || "",
		});
		fetchSchoolGrades(school.id);
		setOpenDialog(true);
	};

	const handleAddNew = () => {
		if (isFunmiyoEmployee) return;
		setSelectedSchool(null);
		setEditForm({
			name: "",
			address: "",
			school_type_id: "",
			district: "",
			website_link: "",
			landline: "",
			principal_name: "",
			principal_contact: "",
			finance_incharge: "",
			store_incharge: "",
			finance_incharge_contact: "",
			store_incharge_contact: "",
			primary_email: "",
			status: "Active",
			map_location: "",
			selected_grades: [],
			school_unique_id: "",
			state: "",
			block: "",
			accounts_incharge: "",
			accounts_incharge_number: "",
			total_kits_ordered: "",
			syllabus: "",
			audio: "",
		});
		// Reset form errors
		setFormErrors({
			name: false,
			address: false,
			school_type_id: false,
			state: false,
			district: false,
			selected_grades: false,
		});
		setFormErrorMessage("");
		setOpenDialog(true);
	};

	const handleStatusChange = async (schoolId, newStatus) => {
		if (isFunmiyoEmployee) return;
		try {
			const updates = {
				status: newStatus,
				...(newStatus === "Active"
					? { activated_at: new Date().toISOString() }
					: {}),
				...(newStatus === "Inactive"
					? { deactivated_at: new Date().toISOString() }
					: {}),
			};

			const { error } = await supabase
				.from("schools")
				.update(updates)
				.eq("id", schoolId);

			if (error) throw error;

			setSuccessMessage(
				newStatus === "Active"
					? "School has been activated successfully"
					: "School has been deactivated successfully"
			);

			await fetchSchools(viewerProfile);

			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error("Error updating school status:", err);
		}
	};

	const handleSave = async () => {
		try {
			setIsLoading(true);

			if (isFunmiyoEmployee && !selectedSchool) {
				setFormErrorMessage("You do not have permission to add schools.");
				setIsLoading(false);
				return;
			}

			if (isFunmiyoEmployee && selectedSchool) {
				const allowedIds = await fetchActiveAssignedSchoolIdsForExpert(
					supabase,
					user.id
				);
				if (!allowedIds.includes(selectedSchool.id)) {
					setFormErrorMessage(
						"You can only edit schools with an active assignment to your account."
					);
					setIsLoading(false);
					return;
				}
			}

			// Validate mandatory fields
			const errors = {
				name: identityFieldsLocked ? false : !editForm.name.trim(),
				school_type_id: identityFieldsLocked ? false : !editForm.school_type_id,
				state: identityFieldsLocked ? false : !editForm.state,
				district: identityFieldsLocked ? false : !editForm.district,
				address: !editForm.address.trim(),
				selected_grades: editForm.selected_grades.length === 0,
			};

			setFormErrors(errors);

			// Check if any mandatory field is empty
			if (
				errors.name ||
				errors.school_type_id ||
				errors.state ||
				errors.district ||
				errors.address ||
				errors.selected_grades
			) {
				const missing = [
					errors.name && "School Name",
					errors.school_type_id && "School Type",
					errors.state && "State",
					errors.district && "District",
					errors.address && "Address",
					errors.selected_grades && "Grades",
				].filter(Boolean);
				setFormErrorMessage(
					`Please fill in all required fields: ${missing.join(", ")}`
				);
				dialogContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
				setIsLoading(false);
				return;
			}

			// Clear any previous error messages
			setFormErrorMessage("");

			// Ensure we're using the correct field names that match database columns
			const formData = {
				name: identityFieldsLocked ? selectedSchool.name : editForm.name,
				address: editForm.address,
				school_type_id: identityFieldsLocked
					? selectedSchool.school_type_id
					: editForm.school_type_id,
				// Prefer form values (includes inferred state for legacy rows)
				district: editForm.district || selectedSchool?.district || "",
				website_link: editForm.website_link,
				landline: editForm.landline,
				principal_name: editForm.principal_name,
				principal_contact: editForm.principal_contact,
				finance_incharge: editForm.finance_incharge,
				finance_incharge_contact: editForm.finance_incharge_contact,
				store_incharge: editForm.store_incharge,
				store_incharge_contact: editForm.store_incharge_contact,
				primary_email: editForm.primary_email,
				map_location: editForm.map_location,
				status: editForm.status || "Active",
				school_unique_id: editForm.school_unique_id?.trim()
					? editForm.school_unique_id.trim()
					: null,
				state: editForm.state || selectedSchool?.state || "",
				block: editForm.block,
				accounts_incharge: editForm.accounts_incharge,
				accounts_incharge_number: editForm.accounts_incharge_number,
				total_kits_ordered: editForm.total_kits_ordered
					? parseInt(editForm.total_kits_ordered, 10)
					: null,
				syllabus: editForm.syllabus,
				audio: editForm.audio,
			};

			let schoolId;

			if (selectedSchool) {
				// Update existing school — require a returned row so RLS no-ops aren't silent
				const { data: updatedRows, error } = await supabase
					.from("schools")
					.update(formData)
					.eq("id", selectedSchool.id)
					.select("id");

				if (error) {
					console.error("Error updating school:", error);
					throw error;
				}
				if (!updatedRows?.length) {
					throw new Error(
						"Update was blocked. You may not have permission to edit this school."
					);
				}
				schoolId = selectedSchool.id;
				setSuccessMessage("School updated successfully");
			} else {
				// Add new school
				const { data, error } = await supabase
					.from("schools")
					.insert([formData])
					.select();

				if (error) {
					console.error("Error inserting school:", error);
					throw error;
				}
				schoolId = data[0].id;
				setSuccessMessage("School added successfully");
			}

			// Handle school grades association
			if (schoolId) {
				// First delete all existing grades for this school
				if (selectedSchool) {
					const { error: deleteError } = await supabase
						.from("school_grades")
						.delete()
						.eq("school_id", schoolId);

					if (deleteError) {
						console.error("Error deleting old school grades:", deleteError);
						throw new Error(
							`School saved, but grades could not be updated: ${deleteError.message}`
						);
					}
				}

				// Now insert the new grade associations
				const gradeAssociations = editForm.selected_grades.map((gradeId) => ({
					school_id: schoolId,
					grade_id: gradeId,
				}));

				if (gradeAssociations.length > 0) {
					const { error: insertError } = await supabase
						.from("school_grades")
						.insert(gradeAssociations);

					if (insertError) {
						console.error("Error inserting school grades:", insertError);
						throw new Error(
							`School saved, but grades could not be updated: ${insertError.message}`
						);
					}
				}
			}

			await fetchSchools(viewerProfile);
			setOpenDialog(false);

			// Reset form errors
			setFormErrors({
				name: false,
				address: false,
				school_type_id: false,
				state: false,
				district: false,
				selected_grades: false,
			});
			setFormErrorMessage("");

			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error("Error saving school:", err);
			const raw = err?.message || err?.error_description || "";
			let message = raw || "Failed to save school. Please try again.";
			if (
				raw.includes("schools_school_unique_id_unique") ||
				raw.toLowerCase().includes("duplicate key")
			) {
				message =
					"School Unique ID is already used by another school. Clear it or enter a different ID.";
			}
			setFormErrorMessage(message);
			dialogContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Box>
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{successMessage}
				</Alert>
			)}
			{isFunmiyoEmployee && funmiyoExpertNoSchoolLinks === true && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					No active school assignments found for your account. Ask an administrator
					to assign you in School Ownerships.
				</Alert>
			)}

			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}>
				<Typography variant="h6">Manage Schools</Typography>
				<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
					<TextField
						variant="outlined"
						size="small"
						placeholder="Search all columns including owner"
						value={filters.search}
						onChange={(e) => {
							setFilters({ ...filters, search: e.target.value });
							setPage(0);
						}}
						sx={{ width: 300 }}
						InputProps={{
							startAdornment: (
								<SearchIcon size={20} style={{ marginRight: 8 }} />
							),
						}}
					/>
					{!isFunmiyoEmployee && (
						<FormControl size="small" sx={{ minWidth: 160 }}>
							<InputLabel>Filter by Status</InputLabel>
							<Select
								value={filters.status}
								label="Filter by Status"
								onChange={(e) =>
									setFilters({ ...filters, status: e.target.value })
								}>
								<MenuItem value="">All Status</MenuItem>
								<MenuItem value="Active">Active</MenuItem>
								<MenuItem value="Inactive">Inactive</MenuItem>
								<MenuItem value="Pending Approval">Pending Approval</MenuItem>
							</Select>
						</FormControl>
					)}
					{!isFunmiyoEmployee && (
						<Button
							variant="contained"
							startIcon={<PlusIcon size={20} />}
							onClick={handleAddNew}>
							Add School
						</Button>
					)}
				</Box>
			</Box>
			<Paper
				elevation={0}
				sx={{
					p: 2,
					border: "1px solid #e5e7eb",
					borderRadius: "8px",
				}}>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								{SORTABLE_COLUMNS.map((column) => (
									<TableCell key={column.key} sx={{ fontWeight: "bold" }}>
										<TableSortLabel
											active={sortConfig.key === column.key}
											direction={
												sortConfig.key === column.key ? sortConfig.direction : "asc"
											}
											onClick={() => handleSort(column.key)}>
											{column.label}
										</TableSortLabel>
									</TableCell>
								))}
								<TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredSchools
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map((school) => (
									<TableRow key={school.id}>
										<TableCell>{school.name}</TableCell>
										<TableCell>{school.school_unique_id || "N/A"}</TableCell>
										<TableCell>{school.school_types?.name || "N/A"}</TableCell>
										<TableCell>{getSchoolPlanners(school)}</TableCell>
										<TableCell>{school.district || "N/A"}</TableCell>
										<TableCell>{getGradeNames(school)}</TableCell>{" "}
										{/* Display grades */}
										<TableCell>{school.principal_name || "N/A"}</TableCell>
										<TableCell>{school.principal_contact || "N/A"}</TableCell>
										<TableCell>
											{school.website_link ? (
												<IconButton
													size="small"
													href={school.website_link}
													target="_blank"
													rel="noopener noreferrer"
													title="Visit Website">
													<Globe size={16} />
												</IconButton>
											) : (
												"N/A"
											)}
										</TableCell>
										<TableCell>
											{school.audio ? (
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
													}}>
													<AudioIcon size={16} />
													<Typography
														variant="caption"
														sx={{
															maxWidth: 100,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}>
														{school.audio}
													</Typography>
												</Box>
											) : (
												"N/A"
											)}
										</TableCell>
										<TableCell>
											{(() => {
												const statusLabel = school.status || "Active";
												const statusColor =
													STATUS_COLORS[statusLabel] || "#6B7280";
												return (
													<Chip
														label={statusLabel}
														size="small"
														sx={{
															bgcolor: `${statusColor}15`,
															color: statusColor,
															fontWeight: 500,
														}}
													/>
												);
											})()}
										</TableCell>
										<TableCell>
											<IconButton
												size="small"
												onClick={() => handleEditClick(school)}
												sx={{ mr: 1 }}>
												<EditIcon size={16} />
											</IconButton>
											{!isFunmiyoEmployee && school.status !== "Active" && (
												<IconButton
													size="small"
													color="success"
													onClick={() =>
														handleStatusChange(school.id, "Active")
													}
													sx={{ mr: 1 }}>
													<CheckIcon size={16} />
												</IconButton>
											)}
											{!isFunmiyoEmployee && school.status !== "Inactive" && (
												<IconButton
													size="small"
													color="error"
													onClick={() =>
														handleStatusChange(school.id, "Inactive")
													}>
													<XIcon size={16} />
												</IconButton>
											)}
										</TableCell>
									</TableRow>
								))}
						</TableBody>
					</Table>
				</TableContainer>

				{filteredSchools.length === 0 && (
					<Box sx={{ py: 4, textAlign: "center" }}>
						<Typography color="text.secondary">
							No schools found matching the search criteria.
						</Typography>
					</Box>
				)}

				<Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
					<TablePagination
						component="div"
						count={filteredSchools.length}
						page={page}
						onPageChange={(event, newPage) => setPage(newPage)}
						rowsPerPage={rowsPerPage}
						onRowsPerPageChange={(event) => {
							setRowsPerPage(parseInt(event.target.value, 10));
							setPage(0);
						}}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Box>
			</Paper>

			<Dialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				maxWidth="md"
				fullWidth>
				<DialogTitle>
					{selectedSchool ? "Edit School" : "Add New School"}
				</DialogTitle>
				<DialogContent ref={dialogContentRef} dividers sx={{ maxHeight: "70vh" }}>
					{formErrorMessage && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{formErrorMessage}
						</Alert>
					)}
					<Grid container spacing={2} sx={{ mt: 1 }}>
						<Grid item xs={12} sm={6}>
							<TextField
								label="School Name"
								fullWidth
								required
								value={editForm.name}
								disabled={identityFieldsLocked}
								InputProps={{ readOnly: identityFieldsLocked }}
								onChange={(e) => {
									setEditForm({ ...editForm, name: e.target.value });
									if (e.target.value.trim()) {
										setFormErrors({ ...formErrors, name: false });
									}
								}}
								error={formErrors.name}
								helperText={formErrors.name ? "School name is required" : ""}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormControl
								fullWidth
								required
								error={formErrors.school_type_id}
								disabled={identityFieldsLocked}>
								<InputLabel>School Type</InputLabel>
								<Select
									value={editForm.school_type_id}
									label="School Type"
									onChange={(e) => {
										setEditForm({
											...editForm,
											school_type_id: e.target.value,
										});
										if (e.target.value) {
											setFormErrors({ ...formErrors, school_type_id: false });
										}
									}}>
									{schoolTypes.map((type) => (
										<MenuItem key={type.id} value={type.id}>
											{type.name}
										</MenuItem>
									))}
								</Select>
								{formErrors.school_type_id && (
									<Typography
										variant="caption"
										color="error"
										sx={{ ml: 2, mt: 0.5 }}>
										School type is required
									</Typography>
								)}
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormControl
								fullWidth
								required
								error={formErrors.state}
								disabled={identityFieldsLocked}>
								<InputLabel>State / UT</InputLabel>
								<Select
									value={editForm.state}
									label="State / UT"
									onChange={(e) => {
										const v = e.target.value;
										setEditForm({ ...editForm, state: v, district: "" });
										if (v) {
											setFormErrors({ ...formErrors, state: false, district: false });
										}
									}}>
									<MenuItem value="">
										<em>Select state</em>
									</MenuItem>
									{stateOptions.map((s) => (
										<MenuItem key={s.stateCode} value={s.stateName}>
											{s.stateName}
										</MenuItem>
									))}
								</Select>
								{formErrors.state && (
									<Typography
										variant="caption"
										color="error"
										sx={{ ml: 2, mt: 0.5 }}>
										State is required
									</Typography>
								)}
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormControl
								fullWidth
								required
								error={formErrors.district}
								disabled={identityFieldsLocked || (!editForm.state && !editForm.district)}>
								<InputLabel>District</InputLabel>
								<Select
									value={editForm.district}
									label="District"
									displayEmpty
									onChange={(e) => {
										setEditForm({ ...editForm, district: e.target.value });
										if (e.target.value) {
											setFormErrors({ ...formErrors, district: false });
										}
									}}>
									<MenuItem value="">
										<em>
											{editForm.state ? "Select district" : "Select state first"}
										</em>
									</MenuItem>
									{districtOptions.map((district) => (
										<MenuItem key={district} value={district}>
											{district}
										</MenuItem>
									))}
								</Select>
								{formErrors.district && (
									<Typography
										variant="caption"
										color="error"
										sx={{ ml: 2, mt: 0.5 }}>
										District is required
									</Typography>
								)}
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Block"
								fullWidth
								value={editForm.block}
								onChange={(e) =>
									setEditForm({ ...editForm, block: e.target.value })
								}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Landline"
								fullWidth
								value={editForm.landline}
								onChange={(e) =>
									setEditForm({ ...editForm, landline: e.target.value })
								}
							/>
						</Grid>

						{/* Grades Multi-select */}
						<Grid item xs={12}>
							<FormControl
								fullWidth
								required
								error={formErrors.selected_grades}>
								<InputLabel>Grades</InputLabel>
								<Select
									multiple
									value={editForm.selected_grades}
									onChange={(e) => {
										setEditForm({
											...editForm,
											selected_grades: e.target.value,
										});
										if (e.target.value.length > 0) {
											setFormErrors({ ...formErrors, selected_grades: false });
										}
									}}
									input={<OutlinedInput label="Grades" />}
									renderValue={(selected) => {
										const selectedGradeNames = selected
											.map((id) => {
												const grade = grades.find((g) => g.id === id);
												return grade ? grade.name : "";
											})
											.filter(Boolean);

										return selectedGradeNames.join(", ");
									}}>
									{grades.map((grade) => (
										<MenuItem key={grade.id} value={grade.id}>
											<Checkbox
												checked={
													editForm.selected_grades.indexOf(grade.id) > -1
												}
											/>
											<ListItemText primary={grade.name} />
										</MenuItem>
									))}
								</Select>
								{formErrors.selected_grades && (
									<FormHelperText error>
										At least one grade is required
									</FormHelperText>
								)}
							</FormControl>
						</Grid>

						<Grid item xs={12}>
							<TextField
								label="Address"
								fullWidth
								required
								multiline
								rows={2}
								value={editForm.address}
								onChange={(e) => {
									setEditForm({ ...editForm, address: e.target.value });
									if (e.target.value.trim()) {
										setFormErrors({ ...formErrors, address: false });
									}
								}}
								error={formErrors.address}
								helperText={formErrors.address ? "Address is required" : ""}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Principal Name"
								fullWidth
								value={editForm.principal_name}
								onChange={(e) =>
									setEditForm({ ...editForm, principal_name: e.target.value })
								}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Principal Contact"
								fullWidth
								value={editForm.principal_contact}
								onChange={(e) =>
									setEditForm({
										...editForm,
										principal_contact: e.target.value,
									})
								}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Finance Incharge"
								fullWidth
								value={editForm.finance_incharge}
								onChange={(e) =>
									setEditForm({ ...editForm, finance_incharge: e.target.value })
								}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Finance Incharge Contact"
								fullWidth
								value={editForm.finance_incharge_contact}
								onChange={(e) =>
									setEditForm({
										...editForm,
										finance_incharge_contact: e.target.value,
									})
								}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Store Incharge"
								fullWidth
								value={editForm.store_incharge}
								onChange={(e) =>
									setEditForm({ ...editForm, store_incharge: e.target.value })
								}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Store Incharge Contact"
								fullWidth
								value={editForm.store_incharge_contact}
								onChange={(e) =>
									setEditForm({
										...editForm,
										store_incharge_contact: e.target.value,
									})
								}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Accounts Incharge"
								fullWidth
								value={editForm.accounts_incharge}
								onChange={(e) =>
									setEditForm({
										...editForm,
										accounts_incharge: e.target.value,
									})
								}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Accounts Incharge Number"
								fullWidth
								value={editForm.accounts_incharge_number}
								onChange={(e) =>
									setEditForm({
										...editForm,
										accounts_incharge_number: e.target.value,
									})
								}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Primary Email"
								fullWidth
								value={editForm.primary_email}
								onChange={(e) =>
									setEditForm({ ...editForm, primary_email: e.target.value })
								}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Website Link"
								fullWidth
								value={editForm.website_link}
								onChange={(e) =>
									setEditForm({ ...editForm, website_link: e.target.value })
								}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Google Maps Location"
								fullWidth
								placeholder="Enter location coordinates or address"
								value={editForm.map_location || ""}
								onChange={(e) =>
									setEditForm({ ...editForm, map_location: e.target.value })
								}
								helperText="Enter coordinates (e.g., 12.9716,77.5946) or full address"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="School Unique ID"
								fullWidth
								value={editForm.school_unique_id}
								onChange={(e) =>
									setEditForm({ ...editForm, school_unique_id: e.target.value })
								}
								helperText="Enter a unique identifier for the school"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormControl fullWidth>
								<InputLabel>Syllabus</InputLabel>
								<Select
									value={editForm.syllabus}
									label="Syllabus"
									onChange={(e) =>
										setEditForm({ ...editForm, syllabus: e.target.value })
									}>
									<MenuItem value="">Select Syllabus</MenuItem>
									{SYLLABUS_OPTIONS.map((syllabus) => (
										<MenuItem key={syllabus} value={syllabus}>
											{syllabus}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={6}>
							<TextField
								label="Total Kits Ordered"
								fullWidth
								type="number"
								value={editForm.total_kits_ordered}
								onChange={(e) =>
									setEditForm({
										...editForm,
										total_kits_ordered: e.target.value,
									})
								}
								helperText="Enter the total number of kits ordered"
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<TextField
								label="Audio File Name"
								fullWidth
								value={editForm.audio}
								onChange={(e) =>
									setEditForm({ ...editForm, audio: e.target.value })
								}
								helperText="Enter the audio file name (e.g., school_audio.mp3)"
								InputProps={{
									startAdornment: (
										<AudioIcon
											size={20}
											style={{ marginRight: 8, color: "#666" }}
										/>
									),
								}}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenDialog(false)} disabled={isLoading}>
						Cancel
					</Button>
					<Button onClick={handleSave} variant="contained" disabled={isLoading}>
						{isLoading
							? selectedSchool
								? "Updating..."
								: "Adding..."
							: selectedSchool
							? "Update"
							: "Add"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ManageSchools;
